/**
 * yoDEV Workplace AI credits (YOD-627): the pure part of the credits UI.
 *
 * Everything here is free of React and services so it can be unit-tested:
 * option catalogue and ordering, the quota -> display mapping, the
 * once-per-period rule for the "AI Monthly used up" notice, and the Stripe
 * return path.
 */
import type { CopilotQuotaInfo } from '@affine/core/modules/cloud';
import { UserFriendlyError } from '@affine/error';
import { AiCreditOption } from '@affine/graphql';
import { i18nTime } from '@affine/i18n';

export interface AiCreditOptionInfo {
  option: AiCreditOption;
  credits: number;
  /** Whole US dollars. */
  priceUsd: number;
  recurring: boolean;
  bestValue: boolean;
}

/**
 * The catalogue, in display order: AI Monthly first (with the badge), then the
 * bigger top-up, then the smaller one. Prices are fixed by the spec (§4.4);
 * the server's Stripe prices are the source of truth at checkout.
 */
export const AI_CREDIT_OPTIONS: readonly AiCreditOptionInfo[] = [
  {
    option: AiCreditOption.Monthly,
    credits: 1100,
    priceUsd: 9,
    recurring: true,
    bestValue: true,
  },
  {
    option: AiCreditOption.TopUp1000,
    credits: 1000,
    priceUsd: 10,
    recurring: false,
    bestValue: false,
  },
  {
    option: AiCreditOption.TopUp500,
    credits: 500,
    priceUsd: 5,
    recurring: false,
    bestValue: false,
  },
];

/** The free allowance when the server does not say (spec §1). */
export const DEFAULT_FREE_ALLOWANCE = 20;

/**
 * Stripe subscription statuses in which the user still holds an AI Monthly
 * subscription. Offering Monthly again in these states would create a second
 * subscription, so the card is hidden.
 */
const PLAN_HELD_STATUSES = new Set(['active', 'trialing', 'past_due']);

/**
 * Statuses in which there is still a subscription worth managing in the
 * Stripe Customer Portal (to cancel, update the card or resume).
 */
const PLAN_MANAGEABLE_STATUSES = new Set([
  'active',
  'trialing',
  'past_due',
  'unpaid',
  'incomplete',
  'paused',
]);

export function hasActiveAiPlan(planStatus: string | null | undefined) {
  return !!planStatus && PLAN_HELD_STATUSES.has(planStatus.toLowerCase());
}

export function canManageAiPlan(planStatus: string | null | undefined) {
  return !!planStatus && PLAN_MANAGEABLE_STATUSES.has(planStatus.toLowerCase());
}

/** The cards to show, in order. Monthly is hidden while a plan is held. */
export function getVisibleCreditOptions(
  planStatus: string | null | undefined
): AiCreditOptionInfo[] {
  const planHeld = hasActiveAiPlan(planStatus);
  return AI_CREDIT_OPTIONS.filter(
    o => !(planHeld && o.option === AiCreditOption.Monthly)
  );
}

/**
 * YOD-636: which option the modal shows as selected (blue border, primary
 * button). The user's pick if it is still visible, else the best-value option,
 * else the first. AI Monthly is hidden while a plan is held, so the fallback
 * matters.
 */
export function resolveSelectedOption(
  options: readonly AiCreditOptionInfo[],
  picked: AiCreditOption | null
): AiCreditOption | null {
  if (picked && options.some(o => o.option === picked)) return picked;
  return (options.find(o => o.bestValue) ?? options[0])?.option ?? null;
}

export type AiUsageDisplay =
  | { kind: 'unlimited' }
  | {
      kind: 'metered';
      allowance: number;
      freeUsed: number;
      freeRemaining: number;
      planCredits: number;
      topupCredits: number;
      credits: number;
      /** Free allowance left plus every credit: what the user can spend now. */
      totalRemaining: number;
      /** Share of the free allowance used, 0-100. */
      freePercentUsed: number;
      hasPlan: boolean;
      canManagePlan: boolean;
      resetsAt: string;
      planRenewsAt: string | null;
    };

const nonNegative = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? Math.max(0, n) : 0;

/** Maps the `copilot.quota` payload to what the usage surfaces render. */
export function getAiUsageDisplay(quota: CopilotQuotaInfo): AiUsageDisplay {
  if (quota.unlimited) {
    return { kind: 'unlimited' };
  }
  const allowance =
    quota.allowance === null || quota.allowance === undefined
      ? DEFAULT_FREE_ALLOWANCE
      : nonNegative(quota.allowance);
  const freeUsed = Math.min(nonNegative(quota.freeUsed), allowance);
  const freeRemaining = allowance - freeUsed;
  const planCredits = nonNegative(quota.planCredits);
  const topupCredits = nonNegative(quota.topupCredits);
  const credits = planCredits + topupCredits;
  // The server's `remaining` is authoritative (it knows about expiry and the
  // legacy allowance); fall back to the sum only if it is missing.
  const totalRemaining =
    typeof quota.remaining === 'number'
      ? nonNegative(quota.remaining)
      : freeRemaining + credits;
  const freePercentUsed =
    allowance === 0 ? 100 : Math.round((freeUsed / allowance) * 100);

  return {
    kind: 'metered',
    allowance,
    freeUsed,
    freeRemaining,
    planCredits,
    topupCredits,
    credits,
    totalRemaining,
    freePercentUsed,
    hasPlan: hasActiveAiPlan(quota.planStatus),
    canManagePlan: canManageAiPlan(quota.planStatus),
    resetsAt: quota.resetsAt,
    planRenewsAt: quota.planRenewsAt,
  };
}

// ---------------------------------------------------------------------------
// "You've used your AI Monthly credits" notice: once per period.
// ---------------------------------------------------------------------------

export const PLAN_EXHAUSTED_NOTICE_STORAGE_PREFIX =
  'yodev:ai-credits:plan-exhausted-notice:';

/**
 * Identifies the period a planExhausted state belongs to. A new plan period
 * (new `planRenewsAt`) or a new free-allowance month (new `resetsAt`) is a new
 * situation with new numbers, so the notice may show once more.
 */
export function planExhaustedPeriodKey(
  quota: Pick<CopilotQuotaInfo, 'planRenewsAt' | 'resetsAt'>
) {
  return `${quota.planRenewsAt ?? 'none'}|${quota.resetsAt}`;
}

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** localStorage, or null where it is unavailable or throws on access. */
export function getSafeLocalStorage(): KeyValueStorage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/**
 * Returns true, and records the period, the first time a planExhausted quota
 * is seen for a period. Storage failures never throw; without storage the
 * `memory` set still keeps it to once per session.
 */
export function claimPlanExhaustedNotice(
  quota: Pick<
    CopilotQuotaInfo,
    'planExhausted' | 'planRenewsAt' | 'resetsAt' | 'unlimited'
  >,
  userId: string,
  storage: KeyValueStorage | null,
  memory: Set<string>
): boolean {
  if (!quota.planExhausted || quota.unlimited) return false;

  const storageKey = PLAN_EXHAUSTED_NOTICE_STORAGE_PREFIX + userId;
  const period = planExhaustedPeriodKey(quota);
  const memoryKey = `${storageKey}=${period}`;
  if (memory.has(memoryKey)) return false;

  let stored: string | null = null;
  try {
    stored = storage?.getItem(storageKey) ?? null;
  } catch {
    stored = null;
  }
  if (stored === period) {
    memory.add(memoryKey);
    return false;
  }

  memory.add(memoryKey);
  try {
    storage?.setItem(storageKey, period);
  } catch {
    // Quota exceeded or blocked storage: the in-memory guard still applies.
  }
  return true;
}

// ---------------------------------------------------------------------------
// Stripe return handling.
// ---------------------------------------------------------------------------

export const AI_CREDITS_RETURN_PARAM = 'ai_credits';

export type AiCreditsReturnState = 'success' | 'cancelled';

export function parseAiCreditsReturn(
  search: string
): AiCreditsReturnState | null {
  const value = new URLSearchParams(search).get(AI_CREDITS_RETURN_PARAM);
  return value === 'success' || value === 'cancelled' ? value : null;
}

/**
 * The path Stripe sends the user back to: the current path and query, minus
 * any stale `ai_credits` flag and the hash (the server appends
 * `?ai_credits=…`, which must not land inside a fragment). Anything that is not
 * a same-origin absolute path falls back to `/`.
 */
export function buildAiCreditsReturnPath(location: {
  pathname: string;
  search: string;
}): string {
  const { pathname } = location;
  if (
    !pathname.startsWith('/') ||
    pathname.startsWith('//') ||
    pathname.includes('\\')
  ) {
    return '/';
  }
  const params = new URLSearchParams(location.search);
  params.delete(AI_CREDITS_RETURN_PARAM);
  const search = params.toString();
  return search ? `${pathname}?${search}` : pathname;
}

// ---------------------------------------------------------------------------
// Locale formatting.
// ---------------------------------------------------------------------------

/**
 * 1,100 in English, 1.100 in Spanish and Portuguese. `useGrouping: 'always'`
 * matters for Spanish, whose CLDR default leaves four-digit numbers ungrouped.
 */
export function formatCreditCount(n: number, language: string) {
  try {
    return new Intl.NumberFormat(language, { useGrouping: 'always' }).format(n);
  } catch {
    return String(n);
  }
}

/**
 * The monthly allowance resets at 00:00 UTC on the 1st. Rendered in a western
 * timezone that instant is the evening of the last day of the month, which
 * reads as "renews on 30 September" for a month that ends on the 30th. Show
 * the UTC calendar day instead, as a local date so i18nTime formats it as is.
 */
export function utcCalendarDay(iso: string): Date | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/**
 * A day-accuracy date in the UI language, through the shared i18nTime helper.
 * `utcDay` renders the UTC calendar day (use it for `resetsAt`); leave it off
 * for real instants such as the Stripe period end.
 */
export function formatAiCreditsDate(
  iso: string | null | undefined,
  { utcDay = false }: { utcDay?: boolean } = {}
): string {
  if (!iso) return '';
  const date = utcDay ? utcCalendarDay(iso) : new Date(iso);
  if (!date || Number.isNaN(date.getTime())) return '';
  return i18nTime(date, { absolute: { accuracy: 'day' } });
}

/**
 * Which friendly message a failed checkout or portal call gets. The server
 * rate limiter answers TOO_MANY_REQUEST; everything else (billing not
 * configured, Stripe down, network) is the same "try again later" for the
 * user, and the raw error goes to the console.
 */
export function classifyAiCreditsError(
  error: unknown
): 'rate-limited' | 'other' {
  const friendly = UserFriendlyError.fromAny(error);
  return friendly.name === 'TOO_MANY_REQUEST' || friendly.status === 429
    ? 'rate-limited'
    : 'other';
}
